import { PropsOf } from '@emotion/react';
import * as React from 'react';
import s, { CreateStyledComponent, FilteringStyledOptions } from '@emotion/styled';
import { Product } from '@journeyapps-platform/types-accounts';

type Tags<T extends Theme> = {
  // normal tags
  [Tag in keyof JSX.IntrinsicElements]: CreateStyledComponent<
    {
      theme?: T;
      as?: React.ElementType;
    },
    JSX.IntrinsicElements[Tag]
  >;
};

type Component<T> = {
  <
    C extends React.ComponentType<React.ComponentProps<C>>,
    ForwardedProps extends keyof React.ComponentProps<C> & string = keyof React.ComponentProps<C> & string
  >(
    component: C,
    options?: FilteringStyledOptions<React.ComponentProps<C>, ForwardedProps>
  ): CreateStyledComponent<
    Pick<PropsOf<C>, ForwardedProps> & {
      theme?: T;
    }
  >;
};

export type CreateStyled<T extends Theme> = Component<T> & Tags<T>;

export function createStyled<T extends Theme>(): CreateStyled<T> {
  return s as any;
}

export interface Theme {
  background: string;
  dialog: {
    background: string;
    errorColor: string;
  };
  text: {
    fontFamily: string;
    primary: string;
    secondary: string;
    highlight: string;
  };
  button: {
    primary: {
      background: string;
      color: string;
      backgroundDisabled: string;
      backgroundHover: string;
      logoBackground: string;
    };
    secondary: {
      borderColor: string;
      errorBorder: string;
      background: string;
      backgroundHover: string;
    };
    row: {
      background: string;
    };
  };
  plans: {
    upgradeOption: {
      selectedBackground: string;
      background: string;
      entryBackground: string;
      entryLabel: string;
      entryInfo: string;
      accent: string;
      unselectedBackground: string;
      link: string;
      terms: string;
      total: string;
      meta: string;
      max: string;
    };
  };
  forms: {
    checkbox: {
      unchecked: string;
      checked: string;
    };
    ruler: string;
    input: {
      background: string;
      color: string;
      error: string;
    };
    panelBackground: string;
    tooltip: {
      background: string;
    };
    icon: {
      color: string;
    };
    toggle: {
      handleColor: string;
      onColor: string;
      offColor: string;
    };
  };
  progressBar: {
    background: string;
    filler: string;
    pulse: string;
  };
  sidebar: {
    color: string;
    activeColor: string;
    background: string;
    backgroundActive: string;
    backgroundHover: string;
    borderColor: string;
  };
  header: {
    color: string;
    background: string;
    backgroundDarker: string;
    borderColor: string;
    orgSelect: string;
  };
  support: {
    user_comment_background: string;
    agent_comment_background: string;
  };
  table: {
    active: string;
    even: string;
    odd: string;
    border: string;
  };
  search: {
    background: string;
    color: string;
  };
  beta: {
    background: string;
    foreground: string;
  };
  select: {
    displayColor: string;
    inputColor: string;
    background: string;
    indicator: string;
    optionSelected: string;
    optionsHover: string;
    border: string;
    borderDisabled: string;
    borderActive: string;
  };
  status: {
    loadingBackgroundColor: string;
    failedBackgroundColor: string;
    successBackgroundColor: string;
  };
  pills: {
    border: string;
  };
}

export const JourneyTheme: Theme = {
  background: '#1b1b1d',
  dialog: {
    background: '#3a3a3c',
    errorColor: '#ffab00'
  },
  support: {
    user_comment_background: '#1c1c1e',
    agent_comment_background: '#303030'
  },
  plans: {
    upgradeOption: {
      background: '#000',
      entryBackground: '#18181a',
      accent: '#009dff',
      entryInfo: 'rgba(255,255,255,0.4)',
      entryLabel: 'rgba(255,255,255,0.7)',
      selectedBackground: 'linear-gradient(143deg, rgb(255, 126, 0) 0%, rgb(255, 0, 120) 49%, rgb(163, 73, 255) 100%)',
      unselectedBackground: 'rgb(40,40,40)',
      terms: 'rgba(255,255,255,0.5)',
      link: '#fff',
      total: '#fff',
      meta: '#fff',
      max: 'rgba(255,255,255,0.4)'
    }
  },
  text: {
    fontFamily: "'Open Sans', sans-serif",
    primary: 'white',
    secondary: '#aeaeb2',
    highlight: '#ffea00'
  },
  beta: {
    background: 'linear-gradient(158deg, #ff1744,#aa00ff)',
    foreground: 'white'
  },
  button: {
    primary: {
      background: 'linear-gradient(158deg,#ff8f00,#ff1744)',
      backgroundHover: 'linear-gradient(158deg,#ff1744,#ff8f00)',
      backgroundDisabled: '#48484a',
      logoBackground: 'linear-gradient(158deg, #828282, #484848)',
      color: 'white'
    },
    secondary: {
      borderColor: '#595959',
      errorBorder: '#ffab00',
      background: 'transparent',
      backgroundHover: '#2c2c2e'
    },
    row: {
      background: '#1c1c1e'
    }
  },
  forms: {
    checkbox: {
      checked: 'white',
      unchecked: 'white'
    },
    ruler: '#48484a',
    input: {
      background: '#1c1c1e',
      color: 'white',
      error: '#ff6f00'
    },
    panelBackground: '#2c2c2e',
    tooltip: {
      background: '#48484a'
    },
    icon: {
      color: '#aeaeb2'
    },
    toggle: {
      onColor: '#ff6f00',
      offColor: '#484848',
      handleColor: '#fff'
    }
  },
  progressBar: {
    background: '#1c1c1e',
    filler: '#ff6f00',
    pulse: '#ffab00'
  },
  sidebar: {
    color: '#fff',
    activeColor: '#ff8f00',
    background: '#161618',
    backgroundHover: 'linear-gradient(158deg,#ff8f00,#ff1744)',
    backgroundActive: 'linear-gradient(158deg,#ff8f00,#ff1744)',
    borderColor: 'rgb(35,35,35)'
  },
  header: {
    orgSelect: '#19191a',
    background: '#1c1c1e',
    backgroundDarker: '#1c1c1e',
    borderColor: 'rgb(35,35,35)',
    color: '#f2f2f7'
  },
  table: {
    active: '#3a3a3c',
    even: 'rgb(29 29 32)',
    odd: '#18181a',
    border: 'rgb(17 17 18)'
  },
  search: {
    background: '#3a3a3c',
    color: '#aeaeb2'
  },
  select: {
    inputColor: '#fff',
    displayColor: '#fff',
    background: '#3a3a3c',
    border: '#aeaeb2',
    borderDisabled: '#636366',
    borderActive: '#fff',
    indicator: '#aeaeb2',
    optionSelected: '#2c2c2e',
    optionsHover: '#48484a'
  },
  status: {
    failedBackgroundColor: '#8D3149',
    loadingBackgroundColor: '#00b4ff',
    successBackgroundColor: '#658600'
  },
  pills: {
    border: '#aa00ff'
  }
};

export const PowerSyncTheme: Theme = {
  ...JourneyTheme,
  forms: {
    ...JourneyTheme.forms,
    input: {
      ...JourneyTheme.forms.input,
      error: '#ff3358'
    }
  },
  button: {
    ...JourneyTheme.button,
    primary: {
      ...JourneyTheme.button.primary,
      logoBackground: 'linear-gradient(45deg, #aa00ff, #0055ff)',
      background: 'linear-gradient(100deg, #aa00ff, #0055ff)',
      backgroundHover: 'linear-gradient(280deg, #aa00ff, #0055ff)'
    }
  }
};

export const getThemeForProduct = (product: Product) => {
  if (product === Product.POWERSYNC) {
    return PowerSyncTheme;
  }
  return JourneyTheme;
};

export const styled = createStyled<Theme>();
